import { cn } from "@do/ui/lib/utils";
import { AnimatePresence } from "motion/react";
import { isValidElement, useCallback, useEffect, useMemo, useState } from "react";
import { useMotionEnabled } from "../../hooks/use-motion-enabled";
import { useChat } from "../../providers/chat-provider";
import { FOOTER_SLOT_EXIT_DURATION_S, FooterSlot } from "./footer-slot";
import { FooterInput } from "./input";
import { FooterLegal } from "./legal";
import { FooterPoweredBy } from "./powered-by";
import { FooterScrollToBottomButton } from "./scroll-to-bottom-button";

export type FooterProps = {
  disabledInput?: boolean;
  focusPreviewInput?: boolean;
  disableLegalSection?: boolean;
};

export const Footer = ({ disabledInput, focusPreviewInput, disableLegalSection }: FooterProps) => {
  const {
    chatbotParams,
    isAtBottom,
    footerContent,
    buttonsContent,
    noFooter,
    selectedLocale,
    defaultLocale,
    messages,
    hasPreviousStep,
  } = useChat();

  const motionEnabled = useMotionEnabled();

  const isUserInputDisabled = chatbotParams.noUserInput || disabledInput;
  const showFooterContent = Boolean(footerContent);
  // `Message` keys the node it registers by the buttons it holds. Reusing that key here
  // makes each set its own presence, so the outgoing one animates away before the next
  // takes the slot — without it every set would land on the same unchanging presence.
  const buttonsContentKey = isValidElement(buttonsContent) ? buttonsContent.key : null;
  const showInput = !isUserInputDisabled && !showFooterContent && !noFooter;
  // `FooterForm` stamps each instance's own key onto the node it registers, so one form
  // giving way to the next is two presences rather than one with swapped content.
  const footerContentKey = isValidElement(footerContent) ? footerContent.key : null;

  const slotKey = showFooterContent
    ? footerContentKey ?? "footer-slot-form"
    : showInput
    ? "footer-slot-input"
    : null;

  const [settledSlotKey, setSettledSlotKey] = useState(slotKey);
  const isSlotSettling = motionEnabled && slotKey !== settledSlotKey;
  const handleSlotSettled = useCallback(() => setSettledSlotKey(slotKey), [slotKey]);

  // A slot reports itself settled once it has finished arriving — but an EMPTY slot mounts
  // nothing, so there is no one left to report it. With the input disabled, a form leaving
  // behind an empty footer would mark the slot as settling for good and the buttons would
  // never come back.
  //
  // The departure is a plain tween of a known length, so the clock releases them instead:
  // a timer always fires, where a callback on a component that may not exist is exactly
  // what stranded them.
  useEffect(() => {
    if (slotKey !== null || settledSlotKey === null) return;

    const timer = window.setTimeout(
      () => setSettledSlotKey(null),
      FOOTER_SLOT_EXIT_DURATION_S * 1000
    );

    return () => window.clearTimeout(timer);
  }, [slotKey, settledSlotKey]);

  // The buttons sit directly above the slot, so they must not land while it is still
  // moving or they get shoved around by it. Two separate things would put them there:
  //
  // - the slot itself swapping, which they simply wait out;
  // - a form releasing the footer. `FooterForm` clears its content on a microtask, so the
  //   buttons of the message behind it are registered a tick BEFORE the slot learns the
  //   form is gone. Without the `showFooterContent` guard they would surface above the
  //   form still on screen, sink as it is removed, then rise again over the input.
  const showButtonsContent = Boolean(buttonsContent) && !showFooterContent && !isSlotSettling;
  // Hide legal copy after the first user interaction. In normal mode that means
  // a user message is present; with `onlyLastMessage` user messages are never
  // kept in `messages`, so we rely on `hasPreviousStep` (driven by the host
  // history stack) instead. Not `canBackToPreviousStep`: back is switched off
  // around a livechat, which must not bring the legal copy back.
  const hasInteracted = hasPreviousStep ?? messages.some((message) => message.user);
  const showLegalSection = useMemo(
    () =>
      !!(
        (selectedLocale?.disclaimerText ||
          defaultLocale?.disclaimerText ||
          chatbotParams.disclaimerText) &&
        (chatbotParams.disclaimerMessagePlacement === "footer" || chatbotParams.noHeader) &&
        !hasInteracted
      ),
    [
      selectedLocale?.disclaimerText,
      defaultLocale?.disclaimerText,
      chatbotParams.disclaimerText,
      chatbotParams.disclaimerMessagePlacement,
      chatbotParams.noHeader,
      hasInteracted,
    ]
  );

  return (
    <footer
      id="footer"
      className={cn(
        "relative shrink-0 flex flex-col justify-end px-(--footer-padding-x) w-full mx-auto max-w-(--footer-max-width)",
        (!chatbotParams.showPoweredByDialonce || noFooter) && "pb-(--footer-padding-bottom)"
      )}
    >
      <div
        id="footer-gradient"
        className="absolute -top-(--messages-padding-y) inset-x-0 h-(--messages-padding-y) px-(--footer-padding-x) z-1"
      >
        <div
          id="footer-gradient-background"
          className="size-full bg-gradient-to-b from-transparent to-(--background-color)/(--background-color-opacity)"
        />
      </div>

      {/* It sits absolutely above the footer, so holding it on screen while it leaves costs
          the layout nothing. No `initial={false}`: it is never present on the first render
          — `isAtBottom` starts true — so every appearance is a real transition. */}
      <AnimatePresence>
        {!isAtBottom && !noFooter && <FooterScrollToBottomButton />}
      </AnimatePresence>

      {showLegalSection && !disableLegalSection && <FooterLegal />}

      {/* `mode="wait"` lets the outgoing buttons leave before the next arrive: rendering
          both at once would briefly stack two sets and shove the footer around. */}
      <AnimatePresence initial={false} mode="wait">
        {showButtonsContent && (
          <div
            key={buttonsContentKey}
            className={cn(
              "@container/messages",
              (showInput || showFooterContent) && "pb-(--footer-padding-bottom)"
            )}
          >
            {buttonsContent}
          </div>
        )}
      </AnimatePresence>

      {/* The input and a form share one slot and are never shown together, so `mode="wait"`
          gives the sequence the design asks for on its own: whatever is leaving slides
          down and out first, and only then does its replacement rise into the empty slot.
          One child at a time also means the incoming half never has to guess whether the
          outgoing one was an input, a form, or nothing at all.

          No `initial={false}` here, unlike the slot above: opening the chat should play
          the input rising into place, not find it already sitting there.

          Both halves rise from below and retreat downwards, so whichever way the slot
          changes the movement reads as one continuous gesture rather than two unrelated
          ones. */}
      <AnimatePresence mode="wait">
        {showFooterContent ? (
          <FooterSlot key={slotKey} motionEnabled={motionEnabled} onSettled={handleSlotSettled}>
            {footerContent}
          </FooterSlot>
        ) : showInput ? (
          <FooterSlot key={slotKey} motionEnabled={motionEnabled} onSettled={handleSlotSettled}>
            <FooterInput focusPreviewInput={focusPreviewInput} />
          </FooterSlot>
        ) : null}
      </AnimatePresence>

      {chatbotParams.showPoweredByDialonce && !noFooter && <FooterPoweredBy />}
    </footer>
  );
};
